"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"

import { buildFallback, fetchPrices, type PriceData } from "@/lib/pricing"
import { memberPriceChf } from "@/lib/pricing/member-prices"
import { formatNumber } from "@/lib/format"
import { bookingUrl } from "@/lib/booking"
import { useCountry } from "@/components/layout/country-provider"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { isBookingOpen, isComingSoon } from "@/i18n/config"
import { track } from "@/lib/analytics"
import { PersonPackageIcon } from "@/components/ui/package-icons"
import { PricingCard } from "@/components/ui/pricing-card"
import { FollowUpIcon, ImpactAreaIcon } from "@/components/ui/icons"
import { ReviewedByPhysiciansIcon } from "@/components/ui/reviewed-by-physicians-icon"
import {
  ProductInfoButton,
  type BiomarkerGroup,
} from "@/components/ui/product-info-modal"
import { MRI_PACKAGE_INFO } from "@/data/product-info"
import type { PricingCardHighlight } from "@/components/ui/pricing-card"
import { blobMediaUrl } from "@/lib/blob-url"
import { isAllowedInCountry } from "@/lib/country-gates"

/* ───────────────────── Package icon (58px, canonical size) ───────────────────── */

const bgByVariant: Record<string, string> = {
  green: "bg-light-green-200",
  blue: "bg-light-blue-300",
  pink: "bg-aubergine-300",
}

function PackageIconBox({ variant }: { variant: string }) {
  return (
    <div
      className={cn(
        "flex size-[32px] shrink-0 items-center justify-center rounded-full",
        bgByVariant[variant]
      )}
    >
      <PersonPackageIcon size={20} />
    </div>
  )
}

/* ───────────────────── Doctor avatar (34px, canonical size) ───────────────────── */

function DoctorAvatar() {
  return (
    <div className="size-[34px] overflow-hidden rounded-full">
      <Image
        src={blobMediaUrl("/images/avatars/anna-erat.png")}
        alt="Dr. med. Anna Erat"
        width={34}
        height={34}
        className="size-full object-cover"
      />
    </div>
  )
}

/* ───────────────────── Legacy TrustSignals (kept for other consumers) ───────────────────── */

export function TrustSignals({
  reviewedLabel,
  actionPlanLabel,
  videoConsultLabel,
}: {
  reviewedLabel: string
  actionPlanLabel: string
  videoConsultLabel: string
}) {
  return (
    <div className="mt-6 flex justify-between border-t border-neutral-100 pt-6">
      <div className="flex flex-col items-center gap-1 text-center">
        <FollowUpIcon size={24} className="text-neutral-500" />
        <span className="type-body-md text-neutral-500">
          {reviewedLabel.split("\n").map((line, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {line}
            </span>
          ))}
        </span>
      </div>
      <div className="flex flex-col items-center gap-1 text-center">
        <ImpactAreaIcon size={24} className="text-neutral-500" />
        <span className="type-body-md text-neutral-500">
          {actionPlanLabel.split("\n").map((line, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {line}
            </span>
          ))}
        </span>
      </div>
      <div className="flex flex-col items-center gap-1 text-center">
        <Image
          src={blobMediaUrl("/images/avatars/anna-erat.png")}
          alt="Dr. med. Anna Erat"
          width={24}
          height={24}
          className="h-6 w-6 rounded-full object-cover"
        />
        <span className="type-body-md text-neutral-500">
          {videoConsultLabel.split("\n").map((line, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {line}
            </span>
          ))}
        </span>
      </div>
    </div>
  )
}

/* ───────────────────── Package data ───────────────────── */

export const MRI_PACKAGES = [
  {
    name: "Core",
    duration: "45 min",
    descriptionKey: "pkgCoreDescription" as const,
    // Member rate, mirrors MEMBER_PRICING_ITEMS on the membership page (the
    // catalog source of truth). CHF-only.
    memberPrice: "1890",
    priceKey: "core",
    ctaKey: "bookCore" as const,
    includesLabelKey: "pkgCoreIncludesLabel" as const,
    bookingId: "3b7895a5-b892-44f9-9175-c2f7fd8e7962",
    features: [
      { nameKey: "featureFullBodyMri" as const, ai: false },
      { nameKey: "featureBrainMri" as const, ai: false },
      { nameKey: "featureSpineMri" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-core.png"),
    variant: "green" as const,
    highlighted: false,
  },
  {
    name: "Advanced",
    duration: "60 min",
    descriptionKey: "pkgAdvancedDescription" as const,
    memberPrice: "2290", // mirrors the membership catalog
    priceKey: "advanced",
    ctaKey: "bookAdvanced" as const,
    includesLabelKey: "pkgAdvancedIncludesLabel" as const,
    bookingId: "5147c324-1776-4e0a-9cc4-cff5bbcb18c0",
    features: [
      { nameKey: "featureAdvancedBloodTest" as const, ai: false },
      { nameKey: "featureBodyComposition" as const, ai: true },
      { nameKey: "featureProstateOvariesMri" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-advanced.png"),
    variant: "blue" as const,
    highlighted: false,
    mostPopular: true,
  },
  {
    name: "Pro",
    duration: "80 min",
    descriptionKey: "pkgProDescription" as const,
    memberPrice: "3290", // mirrors the membership catalog
    priceKey: "pro",
    ctaKey: "bookPro" as const,
    includesLabelKey: "pkgProIncludesLabel" as const,
    bookingId: "64c6c5cb-95c4-4310-b8b5-ff68abfe7b38",
    features: [
      { nameKey: "featureBrainVolumetry" as const, ai: true },
      { nameKey: "featureHipsKneesMri" as const, ai: false },
      { nameKey: "featureHormonesVitamins" as const, ai: false },
    ],
    image: blobMediaUrl("/images/products/package-pro.png"),
    variant: "pink" as const,
    highlighted: false,
  },
]

/* ───────────────────── Component ───────────────────── */

interface MriPackageCardsProps {
  className?: string
  /** Override live prices — if not passed, fetches from Stripe */
  prices?: PriceData | null
  /** When false, do not trigger a client fetch if `prices` is still null */
  fetchIfMissing?: boolean
  /** Lookup function: given a CMS package ID, returns grouped biomarkers */
  biomarkerGroupsFor?: (packageId: string) => BiomarkerGroup[] | undefined
}

export function MriPackageCards({
  className,
  prices: externalPrices,
  fetchIfMissing = true,
  biomarkerGroupsFor,
}: MriPackageCardsProps) {
  const t = useTranslations("pricing")
  const tt = useTranslations("tests")
  const ut = useTranslations("ui")
  const locale = useLocale()
  const { code: country, currency } = useCountry()
  // Membership markets (CHF): lead each card with the member price + the struck
  // regular. Mirrors the membership-page catalog.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  const { openWaitlist } = useWaitlist()
  // MRI is an expansion product: closed (→ waitlist) until the market reaches
  // `launched`, even when the blood check-up is already bookable (`checkup`).
  const mriClosed = !isBookingOpen(country, "mri")
  // In `checkup` markets (blood live, MRI not) the card CTA opens the waitlist
  // modal, so its label reads "join waitlist". In whole-site waitlist markets
  // the existing card label is kept (pre-existing behaviour).
  const mriComingSoon = isComingSoon(country, "mri")
  // Seed state with the country's fallback prices so SSR + first paint
  // already show the correct currency and amounts (e.g. EUR 1'890 on /de/de,
  // not CHF 1'990). `fetchPrices` runs in `useEffect` and overrides with
  // live Stripe data on the CH side; EUR markets keep the fallback.
  const [liveP, setLiveP] = useState<PriceData | null>(
    externalPrices ?? buildFallback(country)
  )

  useEffect(() => {
    if (externalPrices) {
      setLiveP(externalPrices)
      return
    }
    if (fetchIfMissing) {
      fetchPrices(country).then(setLiveP)
    }
  }, [externalPrices, country, fetchIfMissing])

  const highlights: PricingCardHighlight[] = [
    {
      icon: <ReviewedByPhysiciansIcon size={28} className="text-neutral-500" />,
      label: t("trustReviewedByPhysicians"),
    },
    {
      icon: <ImpactAreaIcon size={28} className="text-neutral-500" />,
      label: t("trustPersonalActionPlan"),
    },
    { icon: <DoctorAvatar />, label: t("trustVideoConsult") },
  ]

  return (
    <div className={cn("grid grid-cols-1 gap-6 lg:grid-cols-3", className)}>
      {MRI_PACKAGES.map((pkg) => {
        // `liveP` is seeded with the central fallback table in lib/pricing.ts,
        // so a CHF market always resolves a price here. On EUR markets an
        // absent price means "no authoritative EUR figure yet" — render the
        // empty-price holding line instead of mislabeling a CHF amount with a
        // € symbol.
        const livePrice = liveP?.prices[pkg.priceKey]
        // `prices` are raw numeric strings (kept unformatted so JSON-LD gets a
        // plain number); format here so the displayed amount carries the
        // locale separator (CHF 1'990, not 1990).
        const price = livePrice ? formatNumber(Number(livePrice), country) : ""
        // Default to the visitor's country currency rather than "CHF" so
        // /de/de doesn't briefly render "CHF 1'990" before the EUR fetch
        // resolves (the EUR fetch is short-circuited in pricing.ts and
        // returns the FALLBACK_PRICES_EUR map synchronously, but liveP is
        // null during the first server-rendered paint).
        const cur = liveP?.currency ?? currency
        // In membership markets + CHF the member price leads and the regular is
        // struck above it (PricingCard `originalPrice`). Member rates
        // are sourced from Stripe (piggybacked into the price maps), so they
        // apply on CHF only.
        const showMember =
          isMembershipFirst && cur === "CHF" && Boolean(pkg.memberPrice)
        // Prefer the live Stripe member rate; fall back to the hardcoded literal
        // when Stripe has no member price for this package (e.g. full-body MRI).
        const memberRate =
          memberPriceChf(liveP?.prices ?? {}, pkg.priceKey) ??
          Number(pkg.memberPrice)
        // Format the member rate the same way as the regular (Swiss separator)
        // so the struck regular + member price read consistently.
        const displayPrice = showMember
          ? formatNumber(memberRate, country)
          : price
        const originalPrice = showMember ? price : undefined
        const href = bookingUrl(locale, pkg.bookingId)
        return (
          <PricingCard
            key={pkg.name}
            icon={<PackageIconBox variant={pkg.variant} />}
            name={pkg.name}
            nameInfoSlot={
              MRI_PACKAGE_INFO[pkg.name] ? (
                <ProductInfoButton
                  data={MRI_PACKAGE_INFO[pkg.name]}
                  livePrice={liveP?.fullPrices[pkg.priceKey]}
                  biomarkerGroups={
                    MRI_PACKAGE_INFO[pkg.name].cmsPackageId &&
                    biomarkerGroupsFor
                      ? biomarkerGroupsFor(
                          MRI_PACKAGE_INFO[pkg.name].cmsPackageId!
                        )
                      : undefined
                  }
                />
              ) : undefined
            }
            subtitle={`${t("pkgDurationLabel")}: ${pkg.duration} · ${t("pkgNoRadiation")}`}
            description={t(pkg.descriptionKey)}
            price={displayPrice}
            originalPrice={originalPrice}
            priceNote={showMember ? tt("memberPriceLabel") : undefined}
            currency={cur}
            ctaLabel={mriComingSoon ? ut("joinWaitlist") : t(pkg.ctaKey)}
            ctaVariant={pkg.name === "Advanced" ? "primary" : "primary-alt"}
            // Core lists baseline items ("Includes") → ✓ check.
            // Advanced / Pro list additions ("Everything in …, plus") → +
            // glyph, reinforcing the additive semantics of the header.
            featuresIcon={pkg.name === "Core" ? "check" : "plus"}
            badgeLabel={
              mriComingSoon
                ? ut("comingSoon")
                : "mostPopular" in pkg && pkg.mostPopular
                  ? t("pkgMostPopular")
                  : undefined
            }
            highlights={highlights}
            featuresLabel={t(pkg.includesLabelKey)}
            features={pkg.features.map((f) => ({
              label: t(f.nameKey),
              aiBadge: f.ai,
            }))}
            aiBadgeLabel={t("aiBadge")}
            onCtaClick={() => {
              if (mriClosed) {
                openWaitlist()
                track.ctaClick(`mri-package-${pkg.name}`, "pricing", "waitlist")
                return
              }
              window.location.href = href
            }}
          />
        )
      })}
    </div>
  )
}
